import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, X, Check, Download } from 'lucide-react';
import * as api from '../lib/api';
import { formatPrice } from '../lib/eventsFormat';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PurchaseForm = ({ item, onPaid, busyRef }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const amount = item.your_price_cents;
  const discount = item.price_cents - amount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !stripe || !elements) return;
    setLoading(true);
    busyRef.current = true;
    setError('');
    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card: elements.getElement(CardElement) });
      if (pmError) { setError(pmError.message); return; }
      const res = await api.purchaseLibraryItem(item.id, { payment_method_id: paymentMethod.id });
      const { status, requires_action: requiresAction, client_secret: clientSecret, payment_intent_id: intentId } = res.data;
      if (status === 'paid') { onPaid(); return; }
      if (requiresAction && clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(clientSecret);
        if (confirmError) { setError(confirmError.message); return; }
        const fin = await api.finalizeLibraryPurchase(item.id, intentId);
        if (fin.data?.status === 'paid') { onPaid(); return; }
      }
      setError('We could not complete your payment. Please try again.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
      busyRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="library-checkout-form">
      <div className="mt-4 text-[15px]">
        <div className="flex justify-between py-2.5 border-b border-dashed border-primary/30"><span>{item.title}</span><span>{formatPrice(item.price_cents)}</span></div>
        {discount > 0 && (
          <div className="flex justify-between py-2.5 border-b border-dashed border-primary/30">
            <span>Sirati+ member discount ({item.discount_pct}%)</span>
            <span className="font-bold text-teal-dark dark:text-teal-light">&minus;${(discount / 100).toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between pt-3.5 font-heading text-xl font-semibold"><span>Total</span><span data-testid="library-checkout-total">${(amount / 100).toFixed(2)}</span></div>
      </div>

      <div className="mt-4">
        <label className="text-sm font-bold text-text-main mb-1.5 block" id="library-card-label">Card details</label>
        <div className="p-4 bg-white rounded-arch border border-primary/20" aria-labelledby="library-card-label">
          <CardElement options={{ style: { base: { fontSize: '16px', color: '#1a1a2e', '::placeholder': { color: '#999' } }, invalid: { color: '#ef4444' } } }} />
        </div>
      </div>

      <div className="mt-4 p-4 rounded-arch bg-lavender/15 text-[13px] leading-relaxed text-text-main" data-testid="library-refund-policy">
        <strong>Refunds:</strong> this is a one-time purchase. It is non-refundable once you have downloaded it. If you have not downloaded it, you can ask for a full refund within 7 days by emailing{' '}
        <a href="mailto:salam@sirati.co" className="text-primary underline">salam@sirati.co</a>.
      </div>

      {error && <p className="text-sm text-red-500 mt-3" role="alert" data-testid="library-checkout-error">{error}</p>}

      <button type="submit" disabled={loading || !stripe}
        className="w-full min-h-[48px] mt-5 py-3 bg-primary text-white rounded-full text-base font-bold btn-hover flex items-center justify-center gap-2 disabled:opacity-50" data-testid="library-pay-btn">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Processing...</> : `Pay $${(amount / 100).toFixed(2)}`}
      </button>
      <p className="text-xs text-text-muted text-center mt-3">
        Payments are processed securely by Stripe. By buying you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">Terms</a>.
      </p>
    </form>
  );
};

const SuccessView = ({ item, onDownload, onDone }) => (
  <div className="text-center py-4" role="status" data-testid="library-purchase-success">
    <div className="rounded-arch-lg bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 px-5 py-7">
      <span className="mx-auto w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center"><Check className="w-7 h-7" strokeWidth={3} aria-hidden="true" /></span>
      <h2 id="library-checkout-title" tabIndex={-1} className="font-heading text-2xl font-semibold text-green-800 dark:text-green-200 mt-4 outline-none">Payment received</h2>
      <p className="text-green-800 dark:text-green-200 mt-1 font-medium">{item.title} is now in your Library.</p>
      <p className="text-sm text-green-700 dark:text-green-300 mt-3">Check your email for your receipt.</p>
    </div>
    <button onClick={onDownload} className="w-full min-h-[48px] mt-5 py-3 bg-primary text-white rounded-full text-base font-bold btn-hover inline-flex items-center justify-center gap-2" data-testid="library-download-now">
      <Download className="w-5 h-5" aria-hidden="true" />Download now
    </button>
    <button onClick={onDone} className="w-full min-h-[48px] mt-3 py-3 rounded-full text-base font-bold border-2 border-teal/40 text-primary hover:bg-teal/10" data-testid="library-purchase-done">Done</button>
  </div>
);

/** Buy one paid Library item. Mirrors the event checkout: card form, price lines, refund policy, green success popup. */
const LibraryCheckout = ({ item, onClose, onPurchased, onDownload }) => {
  const dialogRef = useRef(null);
  const busyRef = useRef(false);
  const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    const focusable = () => dialog.querySelectorAll('button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"]), iframe');
    dialog.querySelector('h2')?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busyRef.current) { onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    const opener = openerRef.current;
    return () => { document.removeEventListener('keydown', onKeyDown); opener?.focus?.(); };
  }, [onClose]);

  const handlePaid = () => { setDone(true); onPurchased?.(); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !busyRef.current && onClose()}>
      <motion.div
        ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="library-checkout-title"
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="glass-card w-full sm:max-w-[500px] max-h-[92vh] overflow-y-auto rounded-t-arch-xl sm:rounded-arch-xl p-6 sm:p-8"
        style={{ background: 'var(--background)' }} onClick={(e) => e.stopPropagation()} data-testid="library-checkout"
      >
        {!done && (
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="library-checkout-title" tabIndex={-1} className="font-heading text-2xl font-semibold text-text-main outline-none">Get this item</h2>
              <p className="text-sm text-text-muted mt-1">One-time purchase. Yours to download any time.</p>
            </div>
            <button onClick={onClose} aria-label="Close checkout" className="min-h-[44px] min-w-[44px] -mt-2 -mr-2 flex items-center justify-center rounded-full hover:bg-primary/10 text-text-muted"><X className="w-5 h-5" aria-hidden="true" /></button>
          </div>
        )}
        {done ? (
          <SuccessView item={item} onDone={onClose} onDownload={() => { onClose(); onDownload?.(item); }} />
        ) : (
          <Elements stripe={stripePromise}><PurchaseForm item={item} onPaid={handlePaid} busyRef={busyRef} /></Elements>
        )}
      </motion.div>
    </div>
  );
};

export default LibraryCheckout;
